/**
 * The pure half of the inbox filter bar: reading a filter value out of the URL,
 * and working out what the platform control is allowed to offer.
 *
 * It sits beside the page rather than inside it so the control and the query
 * that the control drives read the same rules, and so the two rules worth
 * pinning can be tested without a browser. Those rules are that a value the
 * platform does not model never reaches the server, and that the list of
 * platforms follows what is actually in front of the operator rather than a pair
 * written by hand.
 */
import type { SelectOption } from '../../components/Input'
import { CHANNEL, ENABLED_CHANNEL_ORDER, label, options } from '../../lib/labels'
import type { Channel, ClassificationLabel } from '../../lib/types'

/** Every channel this build has a name for, in the order the labels declare. */
const KNOWN_CHANNELS = Object.keys(CHANNEL) as Channel[]
const KNOWN_CHANNEL_VALUES: readonly string[] = KNOWN_CHANNELS

function isChannel(value: string): value is Channel {
  return KNOWN_CHANNEL_VALUES.includes(value)
}

/**
 * A link can be shared, bookmarked or edited by hand, so the channel in it is
 * an untrusted string. Anything that is not a real channel reads as no filter,
 * because the alternative is sending a value the server refuses and showing the
 * operator an error where they asked for a narrower list.
 */
export function parseChannelParam(raw: string | null | undefined): Channel | null {
  if (raw === null || raw === undefined) return null
  return isChannel(raw) ? raw : null
}

/**
 * Options for the platform control: the channels that can run today, then any
 * other channel already visible to this operator, so a channel that starts
 * appearing in the product turns up here on its own.
 *
 * "Visible" means two sources, and it needs both. `GET /channel-accounts`
 * returns only the accounts a non-admin owns, while the thread list is scoped by
 * brand, so a member reading a teammate's threads would otherwise be offered no
 * way to isolate them. Passing the loaded threads' channels closes that.
 *
 * The order comes from the declared channel order rather than from the order
 * the accounts happened to arrive in, so the list never reshuffles under the
 * operator between two loads of the same page.
 *
 * The channel already being filtered on is always in the list, even when no
 * account or thread sits on it any more, because a control that cannot show its
 * own value would read as "every platform" while the threads stayed narrowed.
 *
 * A channel this build cannot name is left out on purpose. The filter travels in
 * the URL, and `parseChannelParam` would refuse the value on the way back in, so
 * offering it would hand the operator a choice that quietly undoes itself on the
 * next load.
 */
/**
 * The platforms the filter row may offer, as channels rather than as options.
 *
 * The same set `channelFilterOptions` builds, and it is the source of it, because
 * the chip row wants the channel itself: it draws that platform's own mark beside
 * the label, and a `{value,label}` pair has thrown the channel away by then.
 *
 * Kept as one reading so the two controls can never offer different platforms.
 */
export function channelFilterChoices(
  accounts: readonly { channel: Channel }[],
  selected: Channel | readonly Channel[] | null = null,
  alsoPresent: readonly Channel[] = [],
): Channel[] {
  const present = new Set<Channel>(accounts.map((account) => account.channel))
  for (const channel of alsoPresent) present.add(channel)
  for (const channel of selected === null ? [] : typeof selected === 'string' ? [selected] : selected) present.add(channel)
  const extras = KNOWN_CHANNELS.filter(
    (channel) => present.has(channel) && !ENABLED_CHANNEL_ORDER.includes(channel),
  )
  return [...ENABLED_CHANNEL_ORDER, ...extras]
}

export function channelFilterOptions(
  accounts: readonly { channel: Channel }[],
  selected: Channel | readonly Channel[] | null = null,
  alsoPresent: readonly Channel[] = [],
): SelectOption[] {
  return channelFilterChoices(accounts, selected, alsoPresent).map((channel) => ({
    value: channel,
    label: label.channel(channel),
  }))
}

/**
 * The accounts the "Sent from" control may offer, given the platform in force.
 *
 * Without this the two controls can be set to contradict each other, because the
 * server ands them: Platform of Email and a LinkedIn account is an offer the
 * filter bar makes and the thread list can never satisfy, and an empty list
 * reads as "there is nothing here" rather than "you asked for something
 * impossible".
 */
export function accountsOnChannel<T extends { channel: Channel }>(
  accounts: readonly T[],
  channel: Channel | readonly Channel[] | null,
): T[] {
  const on = channel === null ? [] : typeof channel === 'string' ? [channel] : channel
  return on.length === 0 ? [...accounts] : accounts.filter((account) => on.includes(account.channel))
}

/**
 * The sender account that survives a change of platform.
 *
 * Dropping a now-impossible account is the point. An account this build has not
 * loaded is kept rather than dropped: the accounts list arrives after the first
 * paint, and a shared link that names an account should not lose it to a race.
 */
export function senderAccountAfterChannelChange(
  accounts: readonly { id: string; channel: Channel }[],
  senderAccountId: string | null,
  channel: Channel | null,
): string | null {
  if (senderAccountId === null || channel === null) return senderAccountId
  const account = accounts.find((candidate) => candidate.id === senderAccountId)
  return account === undefined || account.channel === channel ? senderAccountId : null
}

/**
 * The same rule over lists, which is what the Filters panel holds now that the
 * route takes several of each: the chosen accounts that survive a change of the
 * platforms included. An account on none of them goes, one not loaded yet stays,
 * and no platform included keeps them all.
 */
export function senderAccountsAfterChannelChange(
  accounts: readonly { id: string; channel: Channel }[],
  senderAccountIds: readonly string[],
  channels: readonly Channel[],
): string[] {
  if (channels.length === 0) return [...senderAccountIds]
  return senderAccountIds.filter((id) => {
    const account = accounts.find((candidate) => candidate.id === id)
    return account === undefined || channels.includes(account.channel)
  })
}

// ---------------------------------------------------------------------------
// The three parameters the rail and the tabs write
// ---------------------------------------------------------------------------

/** Which side of the archive the list is reading. Absent is `open`. */
export type InboxStateFilter = 'open' | 'archived' | 'any'

/** The list's three tabs. One at a time, because the server takes one at a time. */
export type InboxView = 'unread' | 'reminders'

const INBOX_STATES: readonly string[] = ['open', 'archived', 'any']
const INBOX_VIEWS: readonly string[] = ['unread', 'reminders']

/**
 * The archive side, read out of the URL.
 *
 * Same rule as `parseChannelParam` above and for the same reason: this travels
 * in a link somebody can edit, and a value the server refuses would show the
 * operator an error where they asked for a narrower list. An unreadable value
 * reads as the default, which is the open inbox.
 *
 * `null` rather than the string `open` is what comes back for the default, so
 * the request builder can leave the parameter off the wire entirely. Sending
 * `state=open` would work and would also put a parameter in every cache key
 * that the ordinary view does not need.
 */
export function parseInboxStateParam(raw: string | null | undefined): InboxStateFilter | null {
  if (raw === null || raw === undefined) return null
  return INBOX_STATES.includes(raw) ? (raw as InboxStateFilter) : null
}

/** The tab, read out of the URL. Anything else reads as the All tab. */
export function parseInboxViewParam(raw: string | null | undefined): InboxView | null {
  if (raw === null || raw === undefined) return null
  return INBOX_VIEWS.includes(raw) ? (raw as InboxView) : null
}

/**
 * A label id out of the URL.
 *
 * Checked for shape rather than taken on trust, because the server answers 400
 * to anything that is not a uuid and the rail would then report an error on a
 * link with a typo in it rather than simply showing the whole inbox. The shape
 * is all this can check: whether the brand owns that label is the server's
 * question and it answers it with a 404.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function parseLabelIdParam(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null
  return UUID.test(raw) ? raw : null
}

/**
 * The two label filters cannot both be on.
 *
 * The server refuses the pair with a 400 rather than guessing, so a link
 * carrying both would break the list instead of narrowing it. One label wins,
 * because it is the more specific of the two questions.
 */
export function labelFiltersFrom(
  labelIdRaw: string | null | undefined,
  unlabelledRaw: string | null | undefined,
): { labelId: string | null; unlabelled: boolean } {
  const labelId = parseLabelIdParam(labelIdRaw)
  if (labelId !== null) return { labelId, unlabelled: false }
  return { labelId: null, unlabelled: unlabelledRaw === 'true' }
}

// ---------------------------------------------------------------------------
// The Filters panel's lists, as the URL carries them
// ---------------------------------------------------------------------------

/** One value or a comma-separated list, from any number of parameters, in order and once each. */
function listItems(raws: readonly (string | null | undefined)[]): string[] {
  const seen: string[] = []
  for (const raw of raws) {
    if (raw === null || raw === undefined) continue
    for (const piece of raw.split(',')) {
      const value = piece.trim()
      if (value !== '' && !seen.includes(value)) seen.push(value)
    }
  }
  return seen
}

/**
 * Sequence or sender ids out of the URL: the new list parameter and the old
 * single one together, because the server reads both and a saved link names
 * the old one. Checked for shape like a label id, so a typo in a shared link
 * reads as no filter rather than a 400 and an error where the list should be.
 */
export function parseIdListParam(...raws: (string | null | undefined)[]): string[] {
  return listItems(raws).filter((value) => UUID.test(value))
}

/** Platforms out of the URL, the same way. A value that is not a real channel is dropped. */
export function parseChannelListParam(...raws: (string | null | undefined)[]): Channel[] {
  return listItems(raws).filter(isChannel)
}

/**
 * The reminder filter's two states, the reference's "Due" and "Scheduled". The
 * server judges both at its own clock: due is a reminder at or before now,
 * scheduled one after it, and both together are every thread with a reminder.
 */
export type ReminderFilter = 'due' | 'scheduled'
export const REMINDER_FILTERS: readonly ReminderFilter[] = ['due', 'scheduled']

export function parseReminderParam(raw: string | null | undefined): ReminderFilter[] {
  const items = listItems([raw])
  return REMINDER_FILTERS.filter((value) => items.includes(value))
}

/**
 * The excluded values that are not also included. The server refuses a value
 * asked for both ways with a 400, and the panel never builds one (a tick in
 * one tab takes the value out of the other), but a hand-edited link can. The
 * include wins, the same way one label wins over No label: it is the more
 * specific question, and a link should narrow the list rather than break it.
 */
export function withoutIncluded<T extends string>(excluded: readonly T[], included: readonly T[]): T[] {
  return excluded.filter((value) => !included.includes(value))
}

/** A list for the address bar and the request: comma-joined, or nothing at all when empty. */
export function listParam(values: readonly string[]): string | null {
  return values.length === 0 ? null : values.join(',')
}

/**
 * How many of the filters behind the Filter control are doing something.
 *
 * WHICH ONES COUNT IS THE WHOLE DECISION. A disclosure has to say what is behind
 * it or it is a box somebody has to open to find out whether it matters, and it
 * must not claim credit for a filter that is already visible: the rail draws its
 * own lit row and the search box holds its own text, so counting those would put
 * a badge on a closed control for something the operator can already see.
 *
 * So this counts exactly the filters with nowhere else to show themselves, one
 * per category whether it includes or excludes: the sequence, the sending
 * account, the platform, the reminder, and the two halves of the admin
 * cross-brand view.
 */
export function hiddenFilterCount(filters: {
  campaignIds?: readonly string[]
  excludeCampaignIds?: readonly string[]
  senderAccountIds?: readonly string[]
  excludeSenderAccountIds?: readonly string[]
  channels?: readonly string[]
  excludeChannels?: readonly string[]
  reminder?: readonly string[]
  brandId?: string | null
  master?: boolean
  mine?: boolean
}): number {
  const some = (...lists: (readonly string[] | undefined)[]) => lists.some((list) => (list?.length ?? 0) > 0)
  const set = [
    some(filters.campaignIds, filters.excludeCampaignIds),
    some(filters.senderAccountIds, filters.excludeSenderAccountIds),
    some(filters.channels, filters.excludeChannels),
    some(filters.reminder),
    filters.brandId !== null && filters.brandId !== undefined && filters.brandId !== '',
  ].filter(Boolean).length
  return set + (filters.master === true ? 1 : 0) + (filters.mine === true ? 1 : 0)
}

/**
 * The classifier label out of the URL.
 *
 * Same rule as the channel and for the same reason, and it matters more now than
 * it did: the rail writes this parameter on every label row, so a stale
 * bookmark from a build with a different vocabulary would hit a 400 and show an
 * error where somebody asked for a narrower list. The server validates this as
 * an enum, so an unknown word is refused rather than ignored.
 *
 * The vocabulary comes from the label table rather than from a list written out
 * here, so a label added to the product turns up without this file being
 * touched.
 */
const KNOWN_CLASSIFICATIONS: readonly string[] = options.classification.map((option) => option.value)

export function parseClassificationParam(
  raw: string | null | undefined,
): ClassificationLabel | null {
  if (raw === null || raw === undefined) return null
  return KNOWN_CLASSIFICATIONS.includes(raw) ? (raw as ClassificationLabel) : null
}

// ---------------------------------------------------------------------------
// Which thread is open, and whether opening it counts as reading it
// ---------------------------------------------------------------------------

/**
 * The thread the reader shows, and whether a person chose it.
 *
 * A requested thread wins even when the list does not hold it yet. Logging a
 * reply navigates to a conversation that only appears after the list refetches,
 * and falling back to the top of the list would land the operator on a
 * different thread than the one they just created.
 *
 * `picked` is the half that matters for read state. A thread id in the query
 * string or the route was written by a person: a row click, j or k, a shared
 * link.
 *
 * SHOWN IS NOT PICKED. From the wide layout up the reader is never empty while
 * the list holds something: with nothing asked for it shows the top of the
 * list, which is how the owner's annotated Primebox opens. That fallback was
 * written by nobody, so `picked` is false and nothing downstream may treat it
 * as a read. (The page once opened nothing on its own, on a reading of an
 * earlier complaint that turned out to be wrong for this owner.)
 *
 * `keepId` is the thread the fallback showed last. While the list still holds
 * it the reader stays on it, so a reply arriving at the top of the list does
 * not swap the thread out from under somebody halfway through a draft.
 *
 * Below the wide layout the list and the thread take turns in one pane, so the
 * page hands over no list there and nothing is shown until somebody opens a row.
 */
export function threadSelection(
  requestedId: string | null | undefined,
  threads: readonly { id: string }[] = [],
  keepId: string | null = null,
): { selectedId: string | null; picked: boolean } {
  if (requestedId !== null && requestedId !== undefined && requestedId !== '') {
    return { selectedId: requestedId, picked: true }
  }
  if (keepId !== null && threads.some((thread) => thread.id === keepId)) {
    return { selectedId: keepId, picked: false }
  }
  return { selectedId: threads[0]?.id ?? null, picked: false }
}

/**
 * Whether to send the mark-read write for the open thread.
 *
 * ONLY FOR A THREAD A PERSON PICKED. The first cut marked whatever was selected,
 * and the selection falls back to the top of the list (it does again, from the
 * wide layout up). On the Unread tab that is a loop with no clicks in it: the
 * top thread is marked read, it leaves the tab, the next one becomes the top
 * and is marked read in turn. A replay against the scratch stack took the brand
 * from 17 unread to 14 in three renders before it was stopped, and left nobody
 * able to tell which threads had been unread.
 *
 * `attempted` is one try per thread per visit. Without it a server that keeps
 * refusing would get one write per render forever.
 */
export function shouldMarkRead(input: {
  selectedId: string | null
  picked: boolean
  unread: boolean | undefined
  attempted: boolean
}): boolean {
  return input.selectedId !== null && input.picked && input.unread === true && !input.attempted
}
